import { t } from "@lingui/macro";
import { useEffect, useRef, useState } from "react";
import { TextInput } from "react-native";

import { Button } from "@whoosh/design-system/src/components/controls";
import { SubtlePhoneInput } from "@whoosh/design-system/src/components/controls/PhoneInput";
import { buildFullPhoneNumber } from "@whoosh/design-system/src/components/controls/PhoneInput/utils/buildPhoneNumber";
import { getCountryCodeById } from "@whoosh/design-system/src/components/controls/PhoneInput/utils/countryCodes";
import { SubtleInput } from "@whoosh/design-system/src/components/controls/SubtleInput";
import {
  VStack,
  Text,
  HStack,
  Box,
} from "@whoosh/design-system/src/components/primitive";

import { useCheckEmailorPhoneProfile } from "../../../hooks/useCheckEmailOrPhoneProfile";
import { isValidToContinue } from "./utils";

/**
 *
 * The initial step is responsible for accepting an email or phone number,
 * it sends that up to check whether we are going to move to entering a password,
 * or verifying our contact information.
 */

type InitialStepProps = {
  defaultCountryCodeId: string;
  updateEmailOrPhone: "phone" | "email" | null;
};

export const InitialStep = ({
  defaultCountryCodeId,
  updateEmailOrPhone,
}: InitialStepProps) => {
  const [currentValue, setCurrentValue] = useState("");

  const defaultCountryCode = getCountryCodeById(defaultCountryCodeId);
  const [currentCountryCode, setCurrentCountryCode] = useState(
    defaultCountryCode?.code?.toString() || "1"
  );
  const canContinue = isValidToContinue(currentValue, currentCountryCode);

  const [checkContactInfo, isChecking] = useCheckEmailorPhoneProfile();

  const handleContinueClick = () => {
    if (canContinue && !isChecking) {
      checkContactInfo({
        value:
          updateEmailOrPhone === "phone"
            ? buildFullPhoneNumber(currentCountryCode, currentValue) || ""
            : currentValue,
        type: updateEmailOrPhone === "phone" ? "Phone" : "Email",
      });
    }
  };

  const inputRef = useRef<TextInput>(null);
  useEffect(() => {
    // Automatically focus the input after swapping
    // this is necessary so that when we flip between phone/email, we don't lose focus.
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [updateEmailOrPhone]);

  return (
    <VStack paddingHorizontal={6} paddingBottom={6} flexGrow={1}>
      <VStack flexGrow={1}>
        <VStack mb={8}>
          <Text variant={"mainTitle2"}>
            {updateEmailOrPhone === "email"
              ? t`Update Your Email`
              : t`Update Your Phone Number`}
          </Text>
        </VStack>
        {updateEmailOrPhone === "phone" ? (
          <SubtlePhoneInput
            placeholder={t`phone`}
            nationalNumber={currentValue}
            defaultCountryId={currentCountryCode}
            onChange={({ countryCode, nationalNumber }) => {
              setCurrentCountryCode(countryCode);
              setCurrentValue(nationalNumber);
            }}
            name={"phone"}
            inputRef={inputRef}
          />
        ) : (
          <SubtleInput
            placeholder={t`Email`}
            name={"Email"}
            value={currentValue}
            onChange={setCurrentValue}
            inputRef={inputRef}
          />
        )}
      </VStack>
      <Box height={120} />
      <VStack gap={6}>
        {updateEmailOrPhone !== "email" ? (
          <HStack justifyContent={"center"}>
            <Text
              variant={"mainSmallMedium"}
              color="text-secondary"
            >{t`Use mobile phone number only`}</Text>
          </HStack>
        ) : null}
        <Button
          accessibilityLabel={t`Continue`}
          isLoading={isChecking}
          isDisabled={!canContinue}
          onPress={handleContinueClick}
        >
          {t`Continue`}
        </Button>
      </VStack>
    </VStack>
  );
};
